import { TextField } from '@material-ui/core'
import { AutocompleteRenderInputParams } from '@material-ui/lab'
import { graphql } from 'babel-plugin-relay/macro'
import { Field, useField, useFormikContext } from 'formik'
import { Autocomplete } from 'formik-material-ui-lab'
import React, { useEffect, useRef, useState } from 'react'
import { fetchQuery, useRelayEnvironment } from 'react-relay/hooks'
import { ExternalVoyageFieldQuery } from '../../__generated__/ExternalVoyageFieldQuery.graphql'
import ItemImage from '../ItemImage'
import { FieldT, FieldVariantEnum } from './types'

interface Props {
  field: FieldT
  variant?: FieldVariantEnum
  withShippingLine?: boolean
}

const ExternalVoyageField: React.FC<Props> & { Skeleton: React.FC } = ({ field, withShippingLine = false }) => {
  const [, meta, helpers] = useField(field.name)
  const { setFieldValue } = useFormikContext()
  const [searchQuery, setSearchQuery] = useState(meta.value || '')
  const searchQueryRef = useRef('')
  const environment = useRelayEnvironment()
  const [options, setOptions] = useState([])

  useEffect(() => {
    fetchQuery<ExternalVoyageFieldQuery>(environment, query, { type: 'voyages', searchQuery: searchQuery })
      .toPromise()
      .then((data) => data?.externalItemList.items.edges?.map((edge) => edge?.node) || [])
      .then((opts) => setOptions(opts as never[]))
  }, [searchQuery, environment])

  const onQueryChange = (newQuery: string) => {
    searchQueryRef.current = newQuery
    setTimeout(() => {
      if (searchQueryRef.current === newQuery) {
        setSearchQuery(newQuery)
      }
    }, 300)
  }

  return (
    <Field
      name={field.name}
      component={Autocomplete}
      options={options}
      getOptionLabel={(option: any) => (typeof option === 'string' ? option : option?.title || '')}
      getOptionSelected={(option: any, value: string) => option.slug === value}
      value={meta.value}
      onChange={(_: any, value: any) => {
        if (value) {
          helpers.setValue(value.slug)
          if (withShippingLine) setFieldValue('shippingLineSlug', value.shippingLineSlug)
        } else {
          helpers.setValue(null)
          if (withShippingLine) setFieldValue('shippingLineSlug', null)
        }
      }}
      onInputChange={(_e: any, value: string) => onQueryChange(value)}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          error={meta.touched && !!meta.error}
          helperText={meta.touched ? meta.error : null}
          label={field.title}
          variant='outlined'
        />
      )}
      renderOption={(option: any) => (
        <>
          <ItemImage data={option.image} variant='small' />
          {option.title}
        </>
      )}
    />
  )
}

ExternalVoyageField.Skeleton = () => <>Loading...</>

const query = graphql`
  query ExternalVoyageFieldQuery($type: ExternalListItemTypeEnum!, $searchQuery: String) {
    externalItemList(type: $type, searchQuery: $searchQuery) {
      items {
        edges {
          node {
            slug
            title
            image {
              ...ItemImage_data
            }
            shippingLineSlug
          }
        }
      }
    }
  }
`

export default ExternalVoyageField
